import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { images } from "../../data/content";
import OrbitPattern from "../OrbitPattern";

const ease = [0.22, 1, 0.36, 1];

const Infrastructure = () => {
  const { isAr, t } = useLang();
  const [activeIndex, setActiveIndex] = useState(0);

  const enPoints = [
    { text: "Two integrated factories", stat: "02", label: "Factories", img: images.woodProduction },
    { text: "Operational area exceeds 22,000 m²", stat: "22K", label: "Operational Area", img: images.factoryInterior },
    { text: "8 specialized production lines", stat: "08", label: "Production Lines", img: images.productionLine },
    { text: "Daily capacity up to 650 doors", stat: "650", label: "Doors / Day", img: images.doorManufacturing },
    { text: "Monthly capacity over 18,000 doors", stat: "+18K", label: "Doors / Month", img: images.warmWoodInterior },
    { text: "More than 200 specialized personnel", stat: "200+", label: "Specialists", img: images.craftsman },
    { text: "Reliable phased supply system", stat: "100%", label: "Reliability", img: images.doorShowroom }
  ];

  const arPoints = [
    { text: "مصنعان متكاملان للإنتاج", stat: "٠٢", label: "مصانع متكاملة", img: images.woodProduction },
    { text: "مساحة تشغيلية تتجاوز ٢٢,٠٠٠ م²", stat: "٢٢ ألف", label: "مساحة تشغيلية", img: images.factoryInterior },
    { text: "٨ خطوط إنتاج صناعية متخصصة", stat: "٠٨", label: "خطوط إنتاج", img: images.productionLine },
    { text: "طاقة يومية تصل إلى ٦٥٠ باب", stat: "٦٥٠", label: "باب يومياً", img: images.doorManufacturing },
    { text: "طاقة شهرية تتجاوز ١٨,٠٠٠ باب", stat: "+١٨ ألف", label: "باب شهرياً", img: images.warmWoodInterior },
    { text: "أكثر من ٢٠٠ كادر صناعي متخصص", stat: "٢٠٠+", label: "كادر متخصص", img: images.craftsman },
    { text: "نظام توريد مرحلي موثوق", stat: "١٠٠٪", label: "اعتمادية التوريد", img: images.doorShowroom }
  ];

  const points = isAr ? arPoints : enPoints;

  return (
    <section
      className="relative bg-brand-green-deep text-bg-ivory py-[120px] md:py-[180px] overflow-hidden"
      data-testid="infrastructure-section"
    >
      <div className="absolute -top-[200px] -end-[200px]">
        <OrbitPattern size={800} opacity={0.06} />
      </div>

      <div className="relative z-10 max-w-container mx-auto px-6 md:px-10 lg:px-14">
        {/* Header */}
        <div className="flex justify-between items-center text-bg-ivory/55 mb-16 border-b border-brand-champagne/20 pb-6" dir={isAr ? "rtl" : "ltr"}>
          <span className="font-mono text-sm tracking-widestest uppercase text-brand-champagne/80">[ {t("البنية التحتية", "Infrastructure")} ]</span>
          <span className="font-mono text-sm">16 — SEC</span>
        </div>

        {/* Section Title & Description */}
        <div className={`grid grid-cols-12 gap-y-8 gap-x-12 mb-16 md:mb-24 ${isAr ? "text-right" : "text-left"}`} dir={isAr ? "rtl" : "ltr"}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease }}
            className="col-span-12 lg:col-span-8"
          >
            <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-champagne/80 mb-5`}>
              {t("[ بنية صناعية متقدمة ]", "[ ADVANCED INFRASTRUCTURE ]")}
            </div>
            <h3
              className={`${isAr ? "font-arabic font-medium" : "font-serif"} text-bg-ivory leading-[1.08] tracking-tight`}
              style={{ fontSize: isAr ? "clamp(38px, 4.8vw, 68px)" : "clamp(46px, 5.2vw, 84px)" }}
            >
              {isAr ? "البنية الصناعية والقدرات التشغيلية" : (<>Industrial <em className="font-light text-brand-champagne/95">Infrastructure</em><br />& Operational Capabilities</>)}
            </h3>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.15, ease }}
            className="col-span-12 lg:col-span-4 flex items-end"
          >
            <p className={`text-brand-champagne/70 leading-relaxed text-base max-w-[380px] lg:mb-2 ${isAr ? "font-arabic" : "font-sans"}`}>
              {t(
                "جاهزية كاملة وقدرات لوجستية وصناعية متطورة تخدم المشاريع الاستراتيجية الكبرى في جميع أنحاء المملكة العربية السعودية.",
                "Complete readiness and advanced logistical & industrial capabilities serving major strategic projects across the Kingdom of Saudi Arabia."
              )}
            </p>
          </motion.div>
        </div>

        {/* Interactive Split layout */}
        <div className="grid grid-cols-12 gap-x-8 gap-y-12 lg:gap-x-16 items-stretch">
          
          {/* Left Column (Dynamic Visual Frame) */}
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1 relative min-h-[500px] lg:min-h-[650px] overflow-hidden bg-brand-green-dark rounded-sm group">
            {/* The dynamic image view */}
            <div className="absolute inset-0 w-full h-full overflow-hidden">
              <motion.img
                key={activeIndex}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease }}
                src={points[activeIndex].img}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "saturate(0.68) contrast(1.08) brightness(0.82)" }}
              />
            </div>

            {/* Ambient gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-green-deep/80 via-brand-green-deep/30 to-transparent z-10" />

            {/* Grid accent line */}
            <div className="absolute inset-0 border border-brand-champagne/10 z-15 pointer-events-none m-6" />

            {/* Premium Bento Caption Overlay */}
            <div className="absolute bottom-12 inset-x-12 z-20 flex flex-col md:flex-row md:items-end justify-between gap-6" dir={isAr ? "rtl" : "ltr"}>
              <motion.div 
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease }}
                className="bg-brand-green-deep/85 backdrop-blur-xl border border-brand-champagne/20 p-8 w-full md:w-[280px] rounded-sm"
              >
                <div className="font-serif text-[48px] lg:text-[56px] text-brand-champagne leading-none font-light mb-3 tracking-tight">
                  {points[activeIndex].stat}
                </div>
                <div className={`font-mono text-xs tracking-widest uppercase text-bg-ivory/60 ${isAr ? "font-arabic" : ""}`}>
                  {points[activeIndex].label}
                </div>
              </motion.div>

              {/* Pagination Dots */}
              <div className="flex gap-2.5 items-center mb-4 md:mb-0">
                {points.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={`h-1.5 transition-all duration-500 rounded-none
                      ${activeIndex === idx ? "w-8 bg-brand-champagne" : "w-1.5 bg-brand-champagne/30 hover:bg-brand-champagne/60"}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column (Interactive capability list) */}
          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2 flex flex-col justify-center space-y-2" dir={isAr ? "rtl" : "ltr"}>
            {points.map((p, i) => (
              <div
                key={i}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => setActiveIndex(i)}
                className={`group relative flex items-center justify-between py-5 px-6 border-b border-brand-champagne/10 transition-all duration-500 ease-out cursor-pointer rounded-none
                  ${activeIndex === i ? "bg-brand-champagne/[0.04]" : "hover:bg-brand-champagne/[0.02]"}`}
              >
                {/* Active Border Accent */}
                <div 
                  className={`absolute top-0 bottom-0 ${isAr ? "right-0" : "left-0"} w-[2px] bg-brand-champagne transition-transform duration-500 ease-out origin-center
                    ${activeIndex === i ? "scale-y-100" : "scale-y-0"}`} 
                />

                <div className="flex items-center gap-6">
                  <span className={`font-mono text-sm transition-colors duration-300 
                    ${activeIndex === i ? "text-brand-champagne" : "text-brand-champagne/40 group-hover:text-brand-champagne/70"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={`text-[17px] leading-relaxed transition-all duration-300
                    ${activeIndex === i ? "text-bg-ivory font-medium " + (isAr ? "-translate-x-2" : "translate-x-2") : "text-bg-ivory/60 group-hover:text-bg-ivory/90 " + (isAr ? "group-hover:-translate-x-1" : "group-hover:translate-x-1")}`}
                    style={{ transitionProperty: "color, transform" }}
                  >
                    {p.text}
                  </span>
                </div>

                {/* Arrow indicator */}
                <div className={`transform transition-all duration-500 ease-out
                  ${activeIndex === i ? "opacity-100 translate-x-0" : `opacity-0 ${isAr ? "translate-x-4" : "-translate-x-4"} pointer-events-none`}`}
                >
                  <span className="text-brand-champagne font-light text-lg">
                    {isAr ? "←" : "→"}
                  </span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Infrastructure;
